import React, { useContext, useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import { TextField } from "@fluentui/react";
import { useHistory } from "react-router";
import { getApiUrl } from "../../api-clients";
import {
  ExamApi,
  ExamDto,
  TeacherApi,
  TeacherDto,
} from "../../api-clients/api";
import { SchoolContext } from "../../contexts/SchoolContext";
import { AppContext } from "../../contexts/AppContext";
import { AuthContext } from "../../contexts/AuthContext";
import Loading from "../Shared/Loading";
import NoResult from "../Shared/NoResult";
import SubjectsDropdown from "../Shared/SubjectsDropdown";
import TeachersDropdown from "../Shared/TeachersDropdown";
import FluentUiDatePicker from "../Shared/FluentUiDatePicker";
import LabelFieldValue from "../Shared/LabelFieldValue";
import FormButtons from "../Shared/FormButtons";
import { dateToString } from "../../helper";
import { useTranslation } from "react-i18next";
import { useRole } from "../../hooks/useRole";
import { IDropdownOption } from "@fluentui/react";

interface IProps {
  classroomId: string;
}
export default function ClassroomExams({ classroomId }: IProps) {
  const { t } = useTranslation();
  const { isAdmin, isTeacher } = useRole();
  const { user } = useContext(AuthContext);
  const { accademicYear } = useContext(SchoolContext);
  const { openSnackbar } = useContext(AppContext);
  const history = useHistory();
  const [examApi] = useState<ExamApi>(new ExamApi(getApiUrl()));
  const [teacherApi] = useState<TeacherApi>(new TeacherApi(getApiUrl()));
  const [exams, setExams] = useState<ExamDto[]>([]);
  const [examsLoading, setExamsLoading] = useState<boolean>(true);

  const [adding, setAdding] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [name, setName] = useState<string>("");
  const [date, setDate] = useState<Date>(new Date());
  const [subjectId, setSubjectId] = useState<string | undefined>(undefined);
  const [teacherId, setTeacherId] = useState<string | undefined>(undefined);
  const [ownTeacher, setOwnTeacher] = useState<TeacherDto | null>(null);
  const [ownTeacherLoading, setOwnTeacherLoading] = useState<boolean>(true);

  const canManageExams = isAdmin || isTeacher;

  const loadExams = () => {
    if (accademicYear?.accademicYearId == null) return;
    setExamsLoading(true);
    examApi
      .classroom(classroomId, accademicYear.accademicYearId)
      .then((res) => {
        setExams(res);
        setExamsLoading(false);
      })
      .catch(() => setExamsLoading(false));
  };

  useEffect(loadExams, [examApi, classroomId, accademicYear]);

  // Teachers can't pick an arbitrary TeacherId — exams must be attributed
  // to their own Teacher record (the backend enforces this server-side too).
  useEffect(() => {
    if (isAdmin || !isTeacher) {
      setOwnTeacherLoading(false);
      return;
    }
    setOwnTeacherLoading(true);
    teacherApi
      .getAll(true)
      .then((res) => {
        const mine = (res ?? []).find((tch) => tch.email === user?.email) ?? null;
        setOwnTeacher(mine);
        setTeacherId(mine?.teacherId);
        setOwnTeacherLoading(false);
      })
      .catch(() => setOwnTeacherLoading(false));
  }, [teacherApi, isAdmin, isTeacher, user]);

  const canSave =
    name.trim().length > 0 && teacherId != null && subjectId != null;

  const resetForm = () => {
    setName("");
    setDate(new Date());
    setSubjectId(undefined);
    setAdding(false);
  };

  const saveExam = () => {
    if (!canSave || accademicYear?.accademicYearId == null) return;
    setSaving(true);
    examApi
      .create({
        classroomId: classroomId,
        accademicYearId: accademicYear.accademicYearId,
        teacherId: teacherId,
        subjectId: subjectId,
        name: name,
        date: date,
      } as ExamDto)
      .then(() => {
        setSaving(false);
        resetForm();
        openSnackbar(t("exams.saveSuccess"), "success");
        loadExams();
      })
      .catch(() => {
        setSaving(false);
        openSnackbar(t("common.errorOccurred"), "error");
      });
  };

  if (!canManageExams) {
    return null;
  }

  return (
    <Card>
      <CardHeader
        title={t("exams.title")}
        action={
          !adding && (
            <IconButton
              aria-label={t("exams.addExam")}
              onClick={() => setAdding(true)}
            >
              <AddIcon />
            </IconButton>
          )
        }
      />
      <CardContent>
        {adding && (
          <>
            <Grid container spacing={1} alignItems="flex-end">
              <Grid item sm={3} xs={12}>
                <TextField
                  required
                  label={t("exams.name")}
                  placeholder={t("exams.namePlaceholder")}
                  value={name}
                  onChange={(
                    event: React.FormEvent<
                      HTMLInputElement | HTMLTextAreaElement
                    >,
                    newValue?: string
                  ) => setName(newValue ?? "")}
                />
              </Grid>
              <Grid item sm={3} xs={12}>
                <FluentUiDatePicker
                  required
                  label={t("exams.date")}
                  placeholder={t("exams.date")}
                  value={date}
                  onSelectDate={(d: Date | null | undefined) => {
                    if (d) setDate(d);
                  }}
                />
              </Grid>
              <Grid item sm={3} xs={12}>
                <SubjectsDropdown
                  required
                  label={t("classSchedule.subject")}
                  placeholder={t("classSchedule.selectSubject")}
                  selectedKey={subjectId}
                  onChange={(
                    event: React.FormEvent<HTMLDivElement>,
                    option?: IDropdownOption
                  ) => setSubjectId(option?.key as string)}
                />
              </Grid>
              <Grid item sm={3} xs={12}>
                {isAdmin ? (
                  <TeachersDropdown
                    required
                    label={t("classSchedule.teacher")}
                    placeholder={t("classSchedule.selectTeacher")}
                    selectedKey={teacherId}
                    onChange={(
                      event: React.FormEvent<HTMLDivElement>,
                      option?: IDropdownOption
                    ) => setTeacherId(option?.key as string)}
                  />
                ) : !ownTeacherLoading ? (
                  ownTeacher != null ? (
                    <LabelFieldValue
                      label={t("classSchedule.teacher")}
                      value={`${ownTeacher.firstName ?? ""} ${
                        ownTeacher.lastName ?? ""
                      }`}
                    />
                  ) : (
                    <NoResult />
                  )
                ) : (
                  <Loading />
                )}
              </Grid>
            </Grid>
            <div style={{ marginTop: "8px" }}>
              <FormButtons
                onConfirmClick={saveExam}
                onCancelClick={resetForm}
                disabled={!canSave || saving}
                cancelButtonVisible={true}
                deleteButtonVisible={false}
                confirmText={t("common.save")}
              />
            </div>
            <Divider style={{ margin: "16px 0" }} />
          </>
        )}
        {!examsLoading ? (
          exams.length > 0 ? (
            <List>
              {exams.map((exam) => (
                <div key={exam.examId}>
                  <ListItem
                    button
                    onClick={() => history.push("/exam/" + exam.examId)}
                  >
                    <ListItemText
                      primary={exam.name}
                      secondary={`${exam.subjectName ?? ""} — ${
                        exam.teacherName ?? ""
                      } — ${dateToString(exam.date)}`}
                    />
                  </ListItem>
                  <Divider />
                </div>
              ))}
            </List>
          ) : (
            <NoResult />
          )
        ) : (
          <Loading />
        )}
      </CardContent>
    </Card>
  );
}
